const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;

/**
 * Token counts run to millions within a day, where a fully grouped number
 * ("14,203,884") costs more width than it carries meaning in a KPI strip, a
 * table cell, or a chart tooltip. One decimal is kept so two adjacent models do
 * not both read "14M". Surfaces that can be hovered pair this with
 * `formatTokensExact` in a `title`; the chart tooltip cannot, since it tracks
 * the cursor and never receives one.
 */
export function formatTokensCompact(value: number, locale: string): string {
  const abs = Math.abs(value);

  if (abs < THOUSAND) return value.toLocaleString(locale);

  const [divisor, suffix] =
    abs >= BILLION ? [BILLION, "B"] : abs >= MILLION ? [MILLION, "M"] : [THOUSAND, "K"];

  const scaled = value / divisor;
  // A scaled value at or above 100 has three significant digits already, so the
  // decimal is noise; below that it is the difference between 1.2M and 1.9M.
  const maximumFractionDigits = Math.abs(scaled) >= 100 ? 0 : 1;

  return `${scaled.toLocaleString(locale, { maximumFractionDigits })}${suffix}`;
}

export function formatTokensExact(value: number, locale: string): string {
  return value.toLocaleString(locale);
}

/**
 * Share of the total, for the per-model table. Returns null when there is no
 * total to take a share of, so the caller renders nothing rather than "0%" or
 * "NaN%".
 */
export function formatTokenShare(value: number, total: number, locale: string): string | null {
  if (total <= 0 || value <= 0) return null;
  return `${((value / total) * 100).toLocaleString(locale, {
    maximumFractionDigits: 1,
  })}%`;
}
